import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useLocale } from "../context/LocaleContext";
import { Download, Loader2, LogOut, Package, RefreshCw, User, LifeBuoy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { useAuth, authApi, formatApiError } from "../context/AuthContext";
import { PageTitle } from "../components/Shared";
import Seo from "../components/Seo";
import { formatPrice } from "../mock";

const content = {
  it: {
    title: "Area Clienti",
    hello: "Ciao",
    logout: "Esci",
    tabs: { orders: "Ordini", downloads: "Download", subscriptions: "Abbonamenti", profile: "Profilo", support: "Assistenza" },
    empty: {
      orders: "Non hai ancora ordini.",
      downloads: "Nessun file disponibile: i file dei prodotti acquistati appariranno qui.",
      subscriptions: "Nessun abbonamento attivo.",
    },
    order: "Ordine",
    date: "Data",
    status: { paid: "Pagato", pending: "In attesa" },
    total: "Totale",
    download: "Scarica",
    downloadsUsed: "download utilizzati",
    unlimited: "illimitati",
    limitReached: "Limite download raggiunto",
    subInactive: "Abbonamento non attivo",
    subStatus: { active: "Attivo", trialing: "In prova", canceled: "Annullato", past_due: "Pagamento in ritardo", unpaid: "Non pagato", unknown: "Sconosciuto", incomplete: "Incompleto", incomplete_expired: "Scaduto" },
    renewal: "Prossimo rinnovo",
    cancelScheduled: "Si interromper\u00e0 a fine periodo",
    name: "Nome e cognome",
    phone: "Telefono",
    email: "Email",
    saveProfile: "Salva profilo",
    profileSaved: "Profilo aggiornato",
    changePw: "Cambia password",
    currentPw: "Password attuale",
    newPw: "Nuova password (min 6 caratteri)",
    pwChanged: "Password aggiornata",
    supportIntro: "Hai bisogno di aiuto su un prodotto o un ordine? Invia una richiesta: il nostro team ti risponder\u00e0 via email.",
    subject: "Oggetto",
    relatedOrder: "Ordine di riferimento (opzionale)",
    none: "Nessuno",
    message: "Messaggio",
    send: "Invia richiesta",
    supportSent: "Richiesta inviata! Ti risponderemo via email al pi\u00f9 presto.",
    portalBtn: "Accedi al portale assistenza clienti",
    wait: "Attendere...",
  },
  en: {
    title: "Customer Area",
    hello: "Hello",
    logout: "Sign out",
    tabs: { orders: "Orders", downloads: "Downloads", subscriptions: "Subscriptions", profile: "Profile", support: "Support" },
    empty: {
      orders: "You have no orders yet.",
      downloads: "No files available: files of purchased products will appear here.",
      subscriptions: "No active subscriptions.",
    },
    order: "Order",
    date: "Date",
    status: { paid: "Paid", pending: "Pending" },
    total: "Total",
    download: "Download",
    downloadsUsed: "downloads used",
    unlimited: "unlimited",
    limitReached: "Download limit reached",
    subInactive: "Subscription not active",
    subStatus: { active: "Active", trialing: "Trial", canceled: "Canceled", past_due: "Past due", unpaid: "Unpaid", unknown: "Unknown", incomplete: "Incomplete", incomplete_expired: "Expired" },
    renewal: "Next renewal",
    cancelScheduled: "Will end at period end",
    name: "Full name",
    phone: "Phone",
    email: "Email",
    saveProfile: "Save profile",
    profileSaved: "Profile updated",
    changePw: "Change password",
    currentPw: "Current password",
    newPw: "New password (min 6 characters)",
    pwChanged: "Password updated",
    supportIntro: "Need help with a product or an order? Send a request: our team will reply by email.",
    subject: "Subject",
    relatedOrder: "Related order (optional)",
    none: "None",
    message: "Message",
    send: "Send request",
    supportSent: "Request sent! We will reply by email as soon as possible.",
    portalBtn: "Access the customer support portal",
    wait: "Please wait...",
  },
};

const fmtDate = (iso, lang) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB");
  } catch {
    return iso;
  }
};

const itemName = (n, lang) => (typeof n === "string" ? n : n?.[lang] || n?.it || "");

const OrdersTab = ({ orders, c, lang }) => {
  if (!orders.length) return <p className="idm-acct-empty" data-testid="orders-empty">{c.empty.orders}</p>;
  return (
    <div className="idm-acct-list" data-testid="orders-list">
      {orders.map((o) => (
        <div className="idm-acct-card" key={o.order_id} data-testid="order-card">
          <div className="idm-acct-card-head">
            <strong>{c.order} #{o.order_id.slice(0, 8).toUpperCase()}</strong>
            <span className={`idm-acct-badge ${o.status}`}>{c.status[o.status] || o.status}</span>
          </div>
          <p className="idm-acct-meta">{c.date}: {fmtDate(o.paid_at || o.created_at, lang)} — {c.total}: <strong>{formatPrice(o.total)}</strong></p>
          <ul className="idm-acct-items">
            {o.items.map((i, idx) => (
              <li key={idx}>
                {i.qty} × {itemName(i.name, lang)} {i.subscription ? <em>({i.subscription.interval === "year" ? (lang === "it" ? "abbonamento annuale" : "yearly subscription") : (lang === "it" ? "abbonamento" : "subscription")})</em> : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

const DownloadsTab = ({ downloads: initialDownloads, c, lang }) => {
  const [downloads, setDownloads] = useState(initialDownloads);
  const [busy, setBusy] = useState(null);

  const handleDownload = async (d) => {
    const key = `${d.order_id}-${d.product_id}`;
    if (busy === key) return;
    setBusy(key);
    try {
      const res = await authApi.get(`/account/download/${d.order_id}/${d.product_id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setDownloads((prev) =>
        prev.map((item) => {
          if (item.order_id !== d.order_id || item.product_id !== d.product_id) return item;
          const newUsed = item.downloads_used + 1;
          const limitReached = item.max_downloads && newUsed >= item.max_downloads;
          return { ...item, downloads_used: newUsed, can_download: !limitReached, reason: limitReached ? "limit_reached" : item.reason };
        })
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "it" ? "Errore durante il download" : "Download error"));
    } finally {
      setBusy(null);
    }
  };

  if (!downloads.length) return <p className="idm-acct-empty" data-testid="downloads-empty">{c.empty.downloads}</p>;
  return (
    <div className="idm-acct-list" data-testid="downloads-list">
      {downloads.map((d) => {
        const key = `${d.order_id}-${d.product_id}`;
        return (
          <div className="idm-acct-card idm-acct-dl" key={key} data-testid="download-card">
            <div>
              <strong>{itemName(d.name, lang)}</strong>
              <p className="idm-acct-meta">
                {d.filename} — {d.downloads_used}/{d.max_downloads || c.unlimited} {c.downloadsUsed}
              </p>
              {!d.can_download && (
                <p className="idm-acct-error" style={{ margin: "4px 0 0" }}>
                  {d.reason === "limit_reached" ? c.limitReached : c.subInactive}
                </p>
              )}
            </div>
            {d.can_download && (
              <button
                className="idm-btn-yellow idm-acct-dlbtn"
                onClick={() => handleDownload(d)}
                disabled={busy === key}
                data-testid="download-btn"
                style={{ animation: "none" }}
              >
                {busy === key ? <Loader2 size={15} className="idm-spin" /> : <Download size={15} />} {c.download}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SubscriptionsTab = ({ subs, c, lang }) => {
  if (!subs.length) return <p className="idm-acct-empty" data-testid="subs-empty">{c.empty.subscriptions}</p>;
  return (
    <div className="idm-acct-list" data-testid="subs-list">
      {subs.map((s) => {
        const st = s.subscription?.status || "unknown";
        return (
          <div className="idm-acct-card" key={s.order_id} data-testid="subscription-card">
            <div className="idm-acct-card-head">
              <strong>{s.items.map((i) => itemName(i.name, lang)).join(", ")}</strong>
              <span className={`idm-acct-badge ${st === "active" || st === "trialing" ? "paid" : "off"}`}>
                {c.subStatus[st] || st}
              </span>
            </div>
            <p className="idm-acct-meta">
              {c.order} #{s.order_id.slice(0, 8).toUpperCase()} — {fmtDate(s.created_at, lang)}
              {s.subscription?.current_period_end ? ` — ${c.renewal}: ${fmtDate(new Date(s.subscription.current_period_end * 1000).toISOString(), lang)}` : ""}
            </p>
            {s.subscription?.cancel_at_period_end && <p className="idm-acct-meta"><em>{c.cancelScheduled}</em></p>}
          </div>
        );
      })}
    </div>
  );
};

const ProfileTab = ({ user, setUser, c }) => {
  const [profile, setProfile] = useState({ name: user.name || "", phone: user.phone || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await authApi.put("/account/profile", profile);
      setUser(data);
      toast.success(c.profileSaved);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await authApi.post("/auth/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success(c.pwChanged);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="idm-acct-profile">
      <form onSubmit={saveProfile} noValidate>
        <div className="idm-field">
          <label>{c.email}</label>
          <input value={user.email} disabled data-testid="profile-email" />
        </div>
        <div className="idm-field">
          <label>{c.name}</label>
          <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} required data-testid="profile-name" />
        </div>
        <div className="idm-field">
          <label>{c.phone}</label>
          <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone" />
        </div>
        <button type="submit" className="idm-btn-yellow" style={{ animation: "none" }} disabled={busy} data-testid="profile-save">
          {busy ? <Loader2 size={15} className="idm-spin" /> : null} {c.saveProfile}
        </button>
      </form>

      <form onSubmit={changePw} noValidate style={{ marginTop: 44 }}>
        <h4 className="idm-acct-form-title">{c.changePw}</h4>
        <div className="idm-field">
          <label>{c.currentPw}</label>
          <input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required data-testid="pw-current" />
        </div>
        <div className="idm-field">
          <label>{c.newPw}</label>
          <input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required data-testid="pw-new" />
        </div>
        <button type="submit" className="idm-btn-dark" disabled={pwBusy || pw.new_password.length < 6} data-testid="pw-save">
          {pwBusy ? <Loader2 size={15} className="idm-spin" /> : null} {c.changePw}
        </button>
      </form>
    </div>
  );
};

const SupportTab = ({ orders, c, lang }) => {
  const [form, setForm] = useState({ subject: "", message: "", order_id: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await authApi.post("/account/support", form);
      setSent(true);
      setForm({ subject: "", message: "", order_id: "" });
      toast.success(c.supportSent);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p className="idm-text">{c.supportIntro}</p>
      {sent && (
        <p className="idm-acct-ok" data-testid="support-sent"><CheckCircle2 size={16} /> {c.supportSent}</p>
      )}
      <form onSubmit={submit} noValidate>
        <div className="idm-field">
          <label>{c.subject}</label>
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="support-subject" />
        </div>
        <div className="idm-field">
          <label>{c.relatedOrder}</label>
          <select value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} data-testid="support-order">
            <option value="">{c.none}</option>
            {orders.map((o) => (
              <option key={o.order_id} value={o.order_id}>
                #{o.order_id.slice(0, 8).toUpperCase()} — {o.items.map((i) => itemName(i.name, lang)).join(", ").slice(0, 60)}
              </option>
            ))}
          </select>
        </div>
        <div className="idm-field">
          <label>{c.message}</label>
          <textarea rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="support-message" />
        </div>
        <button type="submit" className="idm-btn-yellow" style={{ animation: "none", marginTop: 16 }} disabled={busy || form.subject.length < 2 || form.message.length < 5} data-testid="support-send">
          {busy ? <Loader2 size={15} className="idm-spin" /> : null} {busy ? c.wait : c.send}
        </button>
      </form>
      <div style={{ marginTop: 16 }}>
        <a
          href="https://ideamediacrm.com"
          target="_blank"
          rel="noreferrer"
          data-testid="support-portal-link"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "10px 22px", background: "#2d2d2d", color: "#ffffff",
            fontFamily: "inherit", fontSize: 14, fontWeight: 600,
            textDecoration: "none", borderRadius: 0, cursor: "pointer",
          }}
        >
          <LifeBuoy size={15} /> {c.portalBtn}
        </a>
      </div>
    </div>
  );
};

const TAB_ICONS = { orders: Package, downloads: Download, subscriptions: RefreshCw, profile: User, support: LifeBuoy };

const AccountPage = () => {
  const { lang } = useShop();
  const { lp } = useLocale();
  const { user, setUser, logout } = useAuth();
  const c = content[lang];
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let active = true;
    setLoading(true);
    Promise.allSettled([
      authApi.get("/account/orders"),
      authApi.get("/account/downloads"),
      authApi.get("/account/subscriptions"),
    ]).then(([o, d, s]) => {
      if (!active) return;
      if (o.status === "fulfilled") setOrders(o.value.data);
      if (d.status === "fulfilled") setDownloads(d.value.data);
      if (s.status === "fulfilled") setSubs(s.value.data);
      setLoading(false);
    });
    return () => { active = false; };
  }, [user]);

  if (user === null) {
    return (
      <div className="idm-loading idm-loading-page"><span className="idm-spinner" /></div>
    );
  }
  if (user === false) {
    return <Navigate to={lp("/account/login")} replace />;
  }

  return (
    <div data-testid="account-page">
      <Seo title={c.title} description={c.title} />
      <PageTitle title={c.title} />
      <div className="idm-container idm-acct">
        <div className="idm-acct-head">
          <p data-testid="account-welcome">{c.hello} <strong>{user.name || user.email}</strong></p>
          <button className="idm-acct-logout" onClick={logout} data-testid="logout-btn">
            <LogOut size={14} /> {c.logout}
          </button>
        </div>

        <div className="idm-acct-tabs" data-testid="account-tabs">
          {Object.keys(c.tabs).map((key) => {
            const Icon = TAB_ICONS[key];
            return (
              <button
                key={key}
                className={tab === key ? "active" : ""}
                onClick={() => setTab(key)}
                data-testid={`account-tab-${key}`}
              >
                <Icon size={14} /> {c.tabs[key]}
              </button>
            );
          })}
        </div>

        <div className="idm-acct-body">
          {loading && tab !== "profile" && tab !== "support" ? (
            <div className="idm-loading"><span className="idm-spinner" /></div>
          ) : (
            <>
              {tab === "orders" && <OrdersTab orders={orders} c={c} lang={lang} />}
              {tab === "downloads" && <DownloadsTab downloads={downloads} c={c} lang={lang} />}
              {tab === "subscriptions" && <SubscriptionsTab subs={subs} c={c} lang={lang} />}
              {tab === "profile" && <ProfileTab user={user} setUser={setUser} c={c} />}
              {tab === "support" && <SupportTab orders={orders} c={c} lang={lang} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
